import { AddRegular, DeleteRegular, EditRegular } from "@fluentui/react-icons";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { IAdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ITableColumn } from "models/ITableColumn";
import { useCallback, useMemo, useState } from "react";
import { EditLabelDialog } from "./EditLabelDialog";
import {
  ILabelEntity,
  ILabelEntityListProps,
} from "./ManagePortfoliosPractices.types";

export const LabelEntityList = ({
  items,
  isLoading,
  entityName,
  emptyStateMessage,
  onCreate,
  onUpdate,
  onDelete,
}: ILabelEntityListProps): JSX.Element => {
  const colStyles = useAdminColumnStyles();

  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    item: ILabelEntity | null;
  }>({ isOpen: false, item: null });

  const [deleteTarget, setDeleteTarget] = useState<ILabelEntity | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const searchFields = useMemo<(keyof ILabelEntity)[]>(() => ["label"], []);

  const { searchQuery, handleSearchChange, filteredItems } = useAdminListSearch(
    {
      items,
      searchFields,
    },
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<ILabelEntity>({
    cacheKey: `${entityName}List`,
    defaultSortColumn: "label",
    defaultDescending: false,
  });

  const columns: ITableColumn<ILabelEntity>[] = useMemo(
    () => [
      {
        columnId: "label",
        displayValue: "Label",
        className: colStyles.normal,
        renderHeaderCell: () => "Label",
        compare: (a, b) => a.label.localeCompare(b.label),
        renderCell: (item) => <span>{item.label}</span>,
      },
    ],
    [colStyles],
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns),
    [filteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination({
    items: sortedItems,
    itemsPerPage: 15,
  });

  const handleOpenCreate = useCallback(() => {
    setDialogState({ isOpen: true, item: null });
  }, []);

  const handleOpenEdit = useCallback((item: ILabelEntity) => {
    setDialogState({ isOpen: true, item });
  }, []);

  const handleDialogDismiss = useCallback(() => {
    setDialogState({ isOpen: false, item: null });
  }, []);

  const handleSaveDialog = useCallback(
    async (label: string) => {
      setIsSaving(true);
      try {
        if (dialogState.item) {
          await onUpdate(dialogState.item.id, label);
        } else {
          await onCreate(label);
        }
        setDialogState({ isOpen: false, item: null });
      } finally {
        setIsSaving(false);
      }
    },
    [dialogState.item, onCreate, onUpdate],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await onDelete(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
    }
  }, [deleteTarget, onDelete]);

  const rowActions: IAdminRowAction<ILabelEntity>[] = useMemo(
    () => [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item) => handleOpenEdit(item),
        disabled: () => isSaving,
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item) => setDeleteTarget(item),
        disabled: () => isSaving,
      },
    ],
    [isSaving, handleOpenEdit],
  );

  return (
    <>
      <AdminListPage<ILabelEntity>
        items={items}
        columns={columns}
        noPadding
        isLoading={isLoading}
        headerActions={[
          {
            label: "Add New",
            icon: <AddRegular aria-hidden="true" />,
            onClick: handleOpenCreate,
            appearance: "primary",
          },
        ]}
        searchPlaceholder={`Search ${entityName.toLowerCase()}s...`}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        rowActions={rowActions}
        getRowId={(item) => item.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        actionsWidth="108px"
        emptyStateMessage={
          searchQuery
            ? `No ${entityName.toLowerCase()}s match the search.`
            : emptyStateMessage
        }
      />
      <EditLabelDialog
        entityName={entityName}
        initialLabel={dialogState.item?.label ?? ""}
        isOpen={dialogState.isOpen}
        isSaving={isSaving}
        onSave={handleSaveDialog}
        onDismiss={handleDialogDismiss}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title={`Delete ${entityName}`}
        message={`Are you sure you want to delete "${deleteTarget?.label}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeleteTarget(null)}
      />
    </>
  );
};
